import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, qs } from "./api";
import { Alert, Button, FormError, Modal, Reg, SelectInput, TextArea, TextInput, fieldErrors, useDebounced, useToast } from "./ui";
import { CONTACT_PREF_LABEL, displayReg, normaliseReg } from "../shared/labels";
import { useIsOwner } from "./auth";

export interface CustomerLite { id: string; name: string; phone?: string | null }
export interface VehicleLite { id: string; registration: string; make?: string | null; model?: string | null }

// ───────── Customer form (create/edit) with duplicate detection ─────────
const emptyCustomer = { name: "", phone: "", email: "", address: "", postcode: "", notes: "", contactPref: "phone", marketingOk: false };

export function CustomerFormModal({ initial, customerId, onClose, onSaved }: { initial?: Partial<typeof emptyCustomer>; customerId?: string; onClose: () => void; onSaved: (id: string) => void }) {
  const [f, setF] = useState({ ...emptyCustomer, ...initial });
  const [dups, setDups] = useState<any[] | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  // Possible matches while typing (same phone/email, or same name), before anything is saved.
  const probe = useDebounced(JSON.stringify({ name: f.name.trim(), phone: f.phone?.trim() || null, email: f.email?.trim() || null, postcode: f.postcode?.trim() || null }), 400);
  const live = useQuery({
    queryKey: ["customer-dup-probe", probe, customerId],
    queryFn: () => api.post<{ candidates: any[] }>("/customers/duplicates", { ...JSON.parse(probe), excludeId: customerId }),
    enabled: (() => { const p = JSON.parse(probe); return p.name.length >= 3 || (p.phone ?? "").replace(/D/g, "").length >= 9 || !!p.email; })(),
  });
  const matches = dups ? [] : live.data?.candidates ?? [];
  const save = useMutation({
    mutationFn: async (confirmNotDuplicate: boolean) => customerId ? (await api.patch(`/customers/${customerId}`, { ...f, confirmNotDuplicate }), { id: customerId }) : api.post<{ id: string }>("/customers", { ...f, confirmNotDuplicate }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["customers"] }); qc.invalidateQueries({ queryKey: ["customer", r.id] }); toast("success", customerId ? "Customer updated" : "Customer added"); onSaved(r.id); },
    onError: (e) => { if (e instanceof ApiError && e.status === 409 && e.details?.duplicates) setDups(e.details.duplicates); },
  });
  const errs = fieldErrors(save.error);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = (e: FormEvent) => { e.preventDefault(); setDups(null); save.mutate(false); };
  return (
    <Modal title={customerId ? "Edit customer" : "New customer"} onClose={onClose} footer={<>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="primary" type="submit" form="customer-form" loading={save.isPending}>Save customer</Button>
    </>}>
      {customerId && <div style={{ marginBottom: 12 }}><Alert kind="info">Corrections update this customer everywhere (jobs, vehicles, quotes). Invoices already issued keep the details printed on them.</Alert></div>}
      <form id="customer-form" onSubmit={submit} className="form-grid">
        <TextInput label="Full name" required full value={f.name} onChange={set("name")} error={errs.name} autoComplete="off" />
        <TextInput label="Phone" type="tel" value={f.phone ?? ""} onChange={set("phone")} error={errs.phone} />
        <TextInput label="Email" type="email" value={f.email ?? ""} onChange={set("email")} error={errs.email} />
        <TextInput label="Address" full value={f.address ?? ""} onChange={set("address")} />
        <TextInput label="Postcode" value={f.postcode ?? ""} onChange={set("postcode")} />
        <SelectInput label="Preferred contact" value={f.contactPref} onChange={set("contactPref")} options={Object.entries(CONTACT_PREF_LABEL).map(([value, label]) => ({ value, label }))} />
        <TextArea label="Notes" full value={f.notes ?? ""} onChange={set("notes")} />
        <label className="check full"><input type="checkbox" checked={f.marketingOk} onChange={(e) => setF({ ...f, marketingOk: e.target.checked })} /> Happy to receive service reminders</label>
      </form>
      {matches.length > 0 && (
        <div style={{ marginTop: 12 }} aria-live="polite">
          <Alert kind={matches.some((d: any) => d.strength === "strong") ? "warn" : "info"} title={customerId ? "Similar customer already on record" : "This customer may already exist"}>
            {customerId ? "Check it is not the same person. The owner can merge duplicates from Customers → Possible duplicates." : "Choose the existing record instead of creating another."}
          </Alert>
          <ul style={{ paddingLeft: 18 }}>
            {matches.slice(0, 5).map((d: any) => (
              <li key={d.id} style={{ margin: "6px 0" }}>
                <strong>{d.name}</strong> {d.phone && <span className="muted">· {d.phone}</span>} {d.postcode && <span className="muted">· {d.postcode}</span>}
                <span className="small muted"> — {d.reasons.join(", ")}</span>
                {!customerId && <> <Button size="sm" variant="primary" onClick={() => onSaved(d.id)}>Use this customer</Button></>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {dups ? (
        <div style={{ marginTop: 12 }}>
          <Alert kind="warn" title={customerId ? "Same phone or email as another customer" : "Possible existing customer"}>Check these before saving. Nothing has been merged.</Alert>
          <ul style={{ paddingLeft: 18 }}>
            {dups.map((d) => (
              <li key={d.id} style={{ margin: "8px 0" }}>
                <strong>{d.name}</strong> {d.phone && <span className="muted">· {d.phone}</span>} {d.email && <span className="muted">· {d.email}</span>}
                <div className="small muted">{d.reasons.join(", ")}</div>
                {!customerId && <Button size="sm" variant="primary" onClick={() => onSaved(d.id)}>Use this customer</Button>}
              </li>
            ))}
          </ul>
          <Button size="sm" onClick={() => save.mutate(true)} loading={save.isPending}>{customerId ? "Not the same person — save anyway" : "It's a different person — create new"}</Button>
        </div>
      ) : save.error ? <FormError error={save.error} /> : null}
    </Modal>
  );
}

// ───────── Customer picker ─────────
export function CustomerPicker({ value, onChange, label = "Customer" }: { value: CustomerLite | null; onChange: (c: CustomerLite | null) => void; label?: string }) {
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const dq = useDebounced(q.trim(), 200);
  const res = useQuery({ queryKey: ["customers", "pick", dq], queryFn: () => api.get(`/customers${qs({ q: dq, limit: 8 })}`), enabled: dq.length >= 2 });
  if (value) {
    return (
      <div className="field">
        <label>{label}</label>
        <div className="row" style={{ minHeight: 44, border: "1px solid var(--grey-300)", borderRadius: 6, padding: "4px 8px 4px 12px" }}>
          <strong>{value.name}</strong>{value.phone && <span className="muted">{value.phone}</span>}<span className="spacer" />
          <Button size="sm" onClick={() => onChange(null)} aria-label={`Change ${label.toLowerCase()}`}>Change</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="field" style={{ position: "relative" }}>
      <label htmlFor="cust-pick">{label}</label>
      <div className="row" style={{ flexWrap: "nowrap" }}>
        <input id="cust-pick" className="input" placeholder="Search name, phone or reg…" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
        <Button onClick={() => setCreating(true)}>+ New</Button>
      </div>
      {dq.length >= 2 && (
        <div className="search-results" style={{ top: 72 }}>
          {res.isLoading && <div className="loading">Searching…</div>}
          {res.data?.customers?.length === 0 && <div className="empty" style={{ padding: 12 }}>No match. <Button size="sm" variant="primary" onClick={() => setCreating(true)}>Create “{dq}”</Button></div>}
          {res.data?.customers?.map((c: any) => (
            <a key={c.id} href="#" onClick={(e) => { e.preventDefault(); onChange({ id: c.id, name: c.name, phone: c.phone }); setQ(""); }}>
              <strong>{c.name}</strong> <span className="muted small">{c.phone ?? ""} {c.vehicles ? `· ${c.vehicles}` : ""}</span>
            </a>
          ))}
        </div>
      )}
      {creating && <CustomerFormModal initial={{ name: /\d/.test(q) ? "" : q, phone: /\d{5,}/.test(q.replace(/\s/g, "")) ? q : "" }} onClose={() => setCreating(false)}
        onSaved={async (id) => { setCreating(false); const d = await api.get(`/customers/${id}`); onChange({ id, name: d.customer.name, phone: d.customer.phone }); }} />}
    </div>
  );
}

// ───────── Vehicle finder: saved records first, then authorised lookup, manual entry always available ─────────
export function VehicleFinder({ customer, onSelected }: { customer: CustomerLite | null; onSelected: (v: VehicleLite, ownerId: string | null) => void }) {
  const [reg, setReg] = useState("");
  const [stage, setStage] = useState<"idle" | "found" | "lookup" | "manual">("idle");
  const [found, setFound] = useState<any>(null);
  const [lookup, setLookup] = useState<any>(null);
  const [lookupErr, setLookupErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState({ make: "", model: "", variant: "", year: "", fuel: "", engineCc: "", colour: "" });
  const toast = useToast();
  const qc = useQueryClient();

  const search = async () => {
    const r = normaliseReg(reg);
    if (r.length < 2) return;
    setBusy(true); setLookup(null); setLookupErr(null); setFound(null);
    try {
      const saved = await api.get(`/vehicles/by-reg/${encodeURIComponent(r)}`);
      if (saved.vehicle) { setFound(saved); setStage("found"); return; }
      try {
        const l = await api.post("/vehicles/lookup", { registration: r });
        setLookup(l.result); setStage("lookup");
      } catch (e) {
        setLookupErr(e as ApiError); setStage("manual");
      }
    } catch (e) {
      toast("error", (e as Error).message);
    } finally { setBusy(false); }
  };

  const create = useMutation({
    mutationFn: (body: any) => api.post<{ id: string }>("/vehicles", body),
    onSuccess: (r, body) => { qc.invalidateQueries({ queryKey: ["vehicles"] }); toast("success", `Vehicle ${displayReg(body.registration)} saved`); onSelected({ id: r.id, registration: normaliseReg(body.registration), make: body.make, model: body.model }, customer?.id ?? null); },
  });

  const confirmLookup = () => create.mutate({
    registration: reg, make: lookup.make, model: lookup.model, variant: lookup.variant, year: lookup.year, fuel: lookup.fuel, engineCc: lookup.engineCc, colour: lookup.colour,
    customerId: customer?.id ?? null, dataSource: `lookup:${lookup.provider}`, lookupData: lookup.raw,
  });
  const saveManual = (e: FormEvent) => {
    e.preventDefault();
    create.mutate({ registration: reg, make: manual.make || null, model: manual.model || null, variant: manual.variant || null, year: manual.year ? Number(manual.year) : null,
      fuel: manual.fuel || null, engineCc: manual.engineCc ? Number(manual.engineCc) : null, colour: manual.colour || null, customerId: customer?.id ?? null, dataSource: "manual" });
  };

  return (
    <div className="stack">
      <div className="row" style={{ flexWrap: "nowrap", alignItems: "flex-end" }}>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="reg-find">Registration</label>
          <input id="reg-find" className="input reg-input" value={reg} placeholder="AB12 CDE" autoComplete="off"
            onChange={(e) => { setReg(e.target.value.toUpperCase()); setStage("idle"); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} />
        </div>
        <Button variant="primary" onClick={search} loading={busy} disabled={normaliseReg(reg).length < 2}>Find</Button>
      </div>

      {stage === "found" && found && (
        <div className="vehicle-card">
          <div className="img">No photo yet</div>
          <div>
            <div className="small muted">Saved vehicle{found.matchedPrevious ? " (matched a previous registration)" : ""}</div>
            <div className="title"><Reg reg={found.vehicle.registration} /> {[found.vehicle.make, found.vehicle.model].filter(Boolean).join(" ")}</div>
            <div className="small" style={{ marginTop: 6 }}>Current owner: <strong>{found.owner?.name ?? "not linked"}</strong></div>
            {customer && found.owner && found.owner.id !== customer.id && (
              <Alert kind="warn" title="Different owner on record">This vehicle is linked to another customer. If it has changed hands, update the owner from the vehicle page — previous jobs stay with the previous owner.</Alert>
            )}
            <div className="row" style={{ marginTop: 10 }}>
              <Button variant="primary" onClick={() => onSelected(found.vehicle, found.owner?.id ?? null)}>Use this vehicle</Button>
            </div>
          </div>
        </div>
      )}

      {stage === "lookup" && lookup && (
        <div className="vehicle-card" aria-label="Confirm vehicle details">
          <div className="img">
            {lookup.imageUrl ? <><img src={lookup.imageUrl} alt="" /><span className="label">Illustrative image</span></> : <>No image<br />(photos are added at check-in)</>}
          </div>
          <div>
            {lookup.synthetic && <Alert kind="warn" title="Synthetic test data">Development mock provider — not a real vehicle record.</Alert>}
            <div className="small muted">Is this the vehicle? · source: {lookup.provider === "dvla_ves" ? "DVLA Vehicle Enquiry Service" : lookup.provider}</div>
            <div className="title"><Reg reg={reg} /> {lookup.year ?? ""} {lookup.make ?? ""} {lookup.model ?? <span className="muted">(model not supplied)</span>}</div>
            <div className="specs">
              {[["Variant", lookup.variant], ["Year", lookup.year], ["Fuel", lookup.fuel], ["Engine", lookup.engineCc ? `${lookup.engineCc} cc` : null], ["Colour", lookup.colour]].map(([k, v]) => (
                <div className="spec" key={k as string}><small>{k}</small>{v ?? <span className="muted">—</span>}</div>
              ))}
            </div>
            {lookup.notes?.map((n: string) => <div key={n} className="small muted" style={{ marginTop: 6 }}>{n}</div>)}
            <div className="row" style={{ marginTop: 12 }}>
              <Button variant="primary" onClick={confirmLookup} loading={create.isPending}>Yes, save this vehicle</Button>
              <Button onClick={() => { setManual({ make: lookup.make ?? "", model: lookup.model ?? "", variant: lookup.variant ?? "", year: lookup.year ? String(lookup.year) : "", fuel: lookup.fuel ?? "", engineCc: lookup.engineCc ? String(lookup.engineCc) : "", colour: lookup.colour ?? "" }); setStage("manual"); }}>No / edit details</Button>
            </div>
          </div>
        </div>
      )}

      {stage === "manual" && (
        <form onSubmit={saveManual} className="card"><div className="card-body">
          {lookupErr && <div style={{ marginBottom: 12 }}><Alert kind={lookupErr.status === 501 ? "info" : "warn"} title={lookupErr.status === 501 ? "Lookup not configured" : "Lookup unavailable"}>{lookupErr.message}</Alert></div>}
          <div className="form-grid">
            <TextInput label="Make" value={manual.make} onChange={(e) => setManual({ ...manual, make: e.target.value })} />
            <TextInput label="Model" value={manual.model} onChange={(e) => setManual({ ...manual, model: e.target.value })} />
            <TextInput label="Variant / trim" value={manual.variant} onChange={(e) => setManual({ ...manual, variant: e.target.value })} />
            <TextInput label="Year of manufacture" inputMode="numeric" value={manual.year} onChange={(e) => setManual({ ...manual, year: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
            <TextInput label="Fuel" value={manual.fuel} onChange={(e) => setManual({ ...manual, fuel: e.target.value })} />
            <TextInput label="Engine (cc)" inputMode="numeric" value={manual.engineCc} onChange={(e) => setManual({ ...manual, engineCc: e.target.value.replace(/\D/g, "") })} />
            <TextInput label="Colour" value={manual.colour} onChange={(e) => setManual({ ...manual, colour: e.target.value })} />
          </div>
          <div className="form-actions"><Button type="submit" variant="primary" loading={create.isPending}>Save vehicle</Button></div>
        </div></form>
      )}
      {create.error ? <FormError error={create.error} /> : null}
    </div>
  );
}

/** Load the vehicles currently owned by a customer, for quick selection. */
export function CustomerVehicles({ customerId, onPick, selectedId }: { customerId: string; onPick: (v: VehicleLite) => void; selectedId?: string | null }) {
  const q = useQuery({ queryKey: ["customer", customerId], queryFn: () => api.get(`/customers/${customerId}`) });
  const vs: any[] = q.data?.vehicles ?? [];
  if (!vs.length) return null;
  return (
    <div className="field">
      <label>Customer's vehicles</label>
      <div className="row">
        {vs.map((v) => (
          <button type="button" key={v.id} className={`chip ${selectedId === v.id ? "active" : ""}`} onClick={() => onPick(v)}>
            {displayReg(v.registration)} {v.make ?? ""} {v.model ?? ""}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Owner-only Delete for any record. Asks the server first: if the record can be deleted it names it exactly and needs
 *  DELETE typed; if not, it explains what is linked and offers the safe alternative (void, cancel, archive…). */
export function DeleteRecordButton({ kind, id, label = "Delete", onDone, size = "sm" }: { kind: string; id: string; label?: string; onDone?: () => void; size?: "sm" }) {
  const [open, setOpen] = useState(false);
  const owner = useIsOwner();
  if (!owner) return null;
  return <>
    <Button size={size} variant="ghost" className="btn-delete" onClick={() => setOpen(true)}>{label}</Button>
    {open && <DeleteRecordModal kind={kind} id={id} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }} />}
  </>;
}

function DeleteRecordModal({ kind, id, onClose, onDone }: { kind: string; id: string; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const check = useQuery({ queryKey: ["delete-check", kind, id], queryFn: () => api.get<any>(`/records/${kind}/${id}/delete-check`), gcTime: 0 });
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busyAlt, setBusyAlt] = useState<string | null>(null);
  const refreshAll = () => qc.invalidateQueries(); // dashboard, lists and totals all refetch
  const del = useMutation({
    mutationFn: () => api.call("DELETE", `/records/${kind}/${id}`, { confirm: "DELETE" }),
    onSuccess: () => { toast("success", `Deleted ${check.data?.label}`); refreshAll(); onDone(); },
  });
  const alt = useMutation({
    mutationFn: (a: any) => api.call(a.method ?? "POST", a.endpoint, { ...(a.body ?? {}), ...(a.needsReason ? { reason } : {}) }),
    onSuccess: (_r, a: any) => { toast("success", `${a.label}: done`); refreshAll(); onDone(); },
    onSettled: () => setBusyAlt(null),
  });
  const d = check.data;
  const title = !d ? "Delete" : d.canDelete ? `Delete ${d.label}?` : d.financial ? `${d.label}: void or correct` : `${d.label} cannot be deleted`;
  const actionable = (d?.alternatives ?? []).filter((a: any) => a.endpoint);
  return (
    <Modal title={title} onClose={onClose} footer={<>
      <Button onClick={onClose}>{d?.canDelete || actionable.length ? "Cancel" : "Close"}</Button>
      {d?.canDelete && <Button variant="danger" disabled={typed !== "DELETE"} loading={del.isPending} onClick={() => del.mutate()}>Delete permanently</Button>}
    </>}>
      {check.isLoading ? <p className="muted">Checking what is linked to this record…</p> : check.isError ? <FormError error={check.error} /> : d && <>
        {d.canDelete ? <>
          <p>This permanently removes <strong>{d.label}</strong>. Nothing else is deleted. A copy is kept in the audit trail.</p>
          <TextInput label="Type DELETE to confirm" value={typed} onChange={(e) => setTyped(e.target.value.toUpperCase())} autoComplete="off" />
        </> : <>
          <Alert kind={d.financial ? "info" : "warn"} title={d.financial ? "Accounting record" : "Linked records"}>
            {d.financial ? <p style={{ margin: 0 }}>{d.blockers[0]}</p>
              : <><p style={{ margin: "0 0 6px" }}>It is linked to:</p><ul style={{ margin: 0, paddingLeft: 18 }}>{d.blockers.map((b: string) => <li key={b}>{b}</li>)}</ul><p style={{ margin: "6px 0 0" }}>Nothing has been deleted.</p></>}
          </Alert>
          {d.alternatives.length > 0 && <div className="stack" style={{ marginTop: 12 }}>
            {d.alternatives.some((a: any) => a.needsReason && a.endpoint) && <TextArea label="Reason (kept in the audit trail)" value={reason} onChange={(e) => setReason(e.target.value)} />}
            {d.alternatives.map((a: any) => <div key={a.label} className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 180 }}><strong>{a.label}</strong><div className="small muted">{a.explain}</div></div>
              {a.endpoint && <Button size="sm" variant={a.needsReason ? "danger" : "secondary"} loading={busyAlt === a.label && alt.isPending}
                disabled={a.needsReason && reason.trim().length < 3} onClick={() => { setBusyAlt(a.label); alt.mutate(a); }}>{a.label}</Button>}
            </div>)}
          </div>}
        </>}
        <FormError error={del.error ?? alt.error} />
      </>}
    </Modal>
  );
}
